// src/components/store/QuickCartComponents/DispenserCard.jsx
import React, { useState, useMemo, useEffect } from "react";
import toast from "react-hot-toast";
import { Loader, RefreshCw } from "lucide-react";
import { useSalesContext } from "../../../contexts/SalesContext";
import { useInventoryPublic } from "../../../hooks/useInventoryPublic";
import { formatPrice } from "../../../scripts/generalScript";

// --- Componentes de UI que ahora se necesitan directamente ---
import Stepper from "../../ui/Stepper";
import DividerComponent from "../../ui/DividerComponent";

// --- Constantes ---
const DISPENSER_PRICE_BEFORE = formatPrice(13000);
const DISPENSER_PRICE_CURRENT = formatPrice(0);

// --- Componente Badge recreado localmente para eliminar la dependencia ---
const Badge = ({ text, bgColor, textColor }) => (
  <div
    className={`px-2 py-1 rounded-md text-xs font-medium ${bgColor} ${textColor}`}
  >
    {text}
  </div>
);

export const DispenserCard = () => {
  const { selectedSugs, selectedDispensers, setSelectedDispensers } =
    useSalesContext();

  const {
    dispensers: dispenserStock,
    isLoaded: isStockLoaded,
    isRefreshing: isUpdatingStock,
    refreshStock,
    validateQuantity,
  } = useInventoryPublic();

  const [isMobile, setIsMobile] = useState(false);

  const MAX_DISPENSERS = 3;

  useEffect(() => {
    const checkBreakpoint = () => setIsMobile(window.innerWidth < 768);
    checkBreakpoint();
    window.addEventListener("resize", checkBreakpoint);
    return () => window.removeEventListener("resize", checkBreakpoint);
  }, []);

  const calculateMaxDispensers = useMemo(() => {
    const stockLimit = dispenserStock || 0;
    const bottleLimit = selectedSugs > 0 ? selectedSugs : MAX_DISPENSERS;
    return Math.min(stockLimit, bottleLimit, MAX_DISPENSERS);
  }, [dispenserStock, selectedSugs]);

  const handleQuantityChange = (newQuantity) => {
    if (!isStockLoaded) {
      toast.error("Esperando información de inventario...");
      return;
    }

    const validation = validateQuantity("dispensers", newQuantity);
    if (!validation.isValid) {
      toast.error(`Solo hay ${validation.max} dispensadores disponibles`);
      return;
    }

    if (newQuantity > calculateMaxDispensers) {
      const message =
        selectedSugs > 0
          ? `Máximo ${calculateMaxDispensers} dispensadores (según botellones agregados)`
          : `Máximo ${MAX_DISPENSERS} dispensadores por pedido`;
      toast.error(message);
      return;
    }

    setSelectedDispensers(newQuantity);
  };

  // --- Datos del producto, antes en productData ---
  const dispenserData = {
    image:
      "/assets/img/landing/cart-dispenser-drops-blue-plan-recambios-botellon-20l-dispensador-pedidos-online-entrega-domicilio-agua-purificada-calidad.png",
    title: "Dispensador Recargable",
    priceFormatted: DISPENSER_PRICE_CURRENT,
    priceDetails: DISPENSER_PRICE_BEFORE,
    isPriceStrikethrough: true,
    description: {
      bold: "Oferta limitada, ",
      regular: `uno por botellón, máx. ${MAX_DISPENSERS} por cliente. `,
    },
    badges: [
      {
        text: "Gratis",
        bgColor: "bg-fuchsia-100",
        textColor: "text-fuchsia-600",
      },
      ...(isStockLoaded
        ? [
            {
              text:
                dispenserStock > 0 ? `${dispenserStock} en stock` : "Sin stock",
              bgColor:
                dispenserStock > 5
                  ? "bg-green-100"
                  : dispenserStock > 0
                  ? "bg-yellow-100"
                  : "bg-red-100",
              textColor:
                dispenserStock > 5
                  ? "text-green-700"
                  : dispenserStock > 0
                  ? "text-yellow-700"
                  : "text-red-700",
            },
          ]
        : [
            {
              text: "Verificando...",
              bgColor: "bg-gray-100",
              textColor: "text-gray-600",
            },
          ]),
    ],
  };

  // --- Bloques de UI fusionados desde ProductCard ---

  const DescriptionBlock = ({ className = "" }) => (
    <div className={`bg-zinc-100 rounded-2xl p-2 md:p-4 ${className}`}>
      <p className="text-neutral-800 text-base font-normal leading-tight">
        {dispenserData.description.bold && (
          <span className="font-medium">{dispenserData.description.bold}</span>
        )}
        {dispenserData.description.regular}
      </p>
    </div>
  );

  const PriceBlock = ({ className = "" }) => (
    <div className={`flex-col justify-start items-start ${className}`}>
      <div className="text-gray-800 font-medium text-2xl">
        ${dispenserData.priceFormatted}
      </div>
      {dispenserData.priceDetails && (
        <div
          className={`text-neutral-600 text-base font-normal leading-none ${
            dispenserData.isPriceStrikethrough ? "line-through" : ""
          }`}
        >
          ${dispenserData.priceDetails}
        </div>
      )}
    </div>
  );

  const BadgesContainer = () => (
    <div className="flex gap-1 flex-wrap">
      {dispenserData.badges.map((badge, index) => (
        <Badge key={index} {...badge} />
      ))}
    </div>
  );
  
  // --- Lógica de renderizado principal movida aquí ---

  const isDisabled = !isStockLoaded || calculateMaxDispensers === 0;

  const MobileLayout = () => (
     <div className="pt-4">
        <div>
          <div className="h-full flex-shrink-0 flex items-center justify-center w-full bg-white rounded-3xl py-4">
            <img
              className="min-w-1/2 max-h-40 object-contain"
              src={dispenserData.image}
              alt={dispenserData.title}
            />
          </div>
          <div className="p-4 bg-neutral-50 rounded-3xl flex flex-col gap-4">
            <div className="text-gray-800 text-lg font-normal leading-tight flex flex-col gap-1">
              <h2 className="text-xl">{dispenserData.title}</h2>
              <BadgesContainer />
            </div>
            <div className="flex gap-2">
              <div className="flex w-full justify-between items-end">
                <PriceBlock className="mt-1" />
                <div className="w-32">
                  <Stepper
                    value={selectedDispensers}
                    onChange={handleQuantityChange}
                    min={0}
                    max={calculateMaxDispensers}
                    disabled={isDisabled}
                  />
                </div>
              </div>
            </div>
            <DividerComponent color="ebebeb" margin={4} size={1} direction="horizontal" />
            <DescriptionBlock className={"bg-gray-100"} />
          </div>
        </div>
     </div>
  );

  const DesktopLayout = () => (
    <div className="pt-4">
        <div className="justify-start items-stretch gap-6 flex rounded-3xl">
          <div className="w-1/3 flex-shrink-0 bg-whiteoverflow-hidden">
            <img className="w-full h-full object-contain" src={dispenserData.image} alt={dispenserData.title} />
          </div>
          <div className="flex-1 flex-col justify-start items-start gap-4 inline-flex">
            <div className="flex-col justify-start items-start gap-1 flex pb-8">
              <div className="text-gray-800 text-2xl font-normal leading-tight">
                {dispenserData.title}
              </div>
              <BadgesContainer />
            </div>
            <div className="flex-col justify-start items-start gap-4 flex w-full">
              <div className="flex justify-between w-full items-end">
                <PriceBlock className="gap-0.5" />
                <div className="w-32">
                  <Stepper
                    value={selectedDispensers}
                    onChange={handleQuantityChange}
                    min={0}
                    max={calculateMaxDispensers}
                    disabled={isDisabled}
                    className="w-32 ml-4"
                  />
                </div>
              </div>
              <DividerComponent size={1} color="ebebeb" margin={8} />
              <DescriptionBlock className="w-full" />
            </div>
          </div>
        </div>
    </div>
  );


  return (
    <div className="relative">
      {isUpdatingStock && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-3xl">
          <div className="flex items-center gap-2 text-blue-600">
            <Loader className="animate-spin" size={20} />
            <span className="text-sm font-medium">
              Actualizando inventario...
            </span>
          </div>
        </div>
      )}

      {/* Renderizado condicional del Layout */}
      {isMobile ? <MobileLayout /> : <DesktopLayout />}

      {!isStockLoaded && (
        <div className="mt-4 text-center">
          <button
            onClick={refreshStock}
            className="text-blue-600 text-sm hover:underline flex items-center gap-1 mx-auto"
            disabled={isUpdatingStock}
          >
            <RefreshCw
              size={14}
              className={isUpdatingStock ? "animate-spin" : ""}
            />
            Actualizar disponibilidad
          </button>
        </div>
      )}
    </div>
  );
};

export default DispenserCard;